// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Button, Drawer, Group, Menu, Stack, Text } from '@mantine/core';
import type { Cms1500Line } from '@polarissleep/shared';
import {
  blankCms1500Line,
  CMS1500_LETTERS,
  CMS1500_MAX_LINES,
  CMS1500_NDC_UNITS,
  CMS1500_PLACES_OF_SERVICE,
  CMS1500_UNIT_TYPES,
  cms1500Number,
} from '@polarissleep/shared';
import { IconArrowRight, IconChevronDown, IconDots, IconPlus } from '@tabler/icons-react';
import type { JSX } from 'react';
import { classes, cx, Field, getPath, Head, MoneyIn, Req, SelectIn, TextIn, useCms1500Editor } from './Cms1500Fields';

/** Line fields edited in the details drawer — used to open it when jumping to an error. */
export const LINE_DETAIL_FIELDS = [
  'charge',
  'units',
  'unitType',
  'epsdt',
  'renderNpi',
  'lineAuth',
  'note',
  'ndc',
  'ndcQty',
  'ndcUnit',
  'labCharge',
  'labProviderId',
];

function money(value: number): string {
  return value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function lineSummary(line: Cms1500Line): string {
  const parts: string[] = [];
  if (line.charge) {
    parts.push(money(cms1500Number(line.charge) ?? 0));
  }
  if (line.units) {
    parts.push(`${line.units} ${line.unitType === 'MJ' ? 'min' : `unit${line.units === '1' ? '' : 's'}`}`);
  }
  if (line.renderNpi) {
    parts.push(`Rendering NPI ${line.renderNpi}`);
  }
  if (line.note) {
    parts.push(`Note: ${line.note}`);
  }
  if (line.ndc) {
    parts.push(`NDC ${line.ndc}`);
  }
  if (line.lineAuth) {
    parts.push(`Prior auth ${line.lineAuth}`);
  }
  if (line.labCharge) {
    parts.push(`Outside lab ${money(cms1500Number(line.labCharge) ?? 0)}`);
  }
  return parts.join(' · ');
}

/** A borderless input inside the service-line grid. */
function CellInput({
  path,
  placeholder,
  maxLength,
  type,
  uppercase,
  label,
}: {
  path: string;
  placeholder?: string;
  maxLength?: number;
  type?: string;
  uppercase?: boolean;
  label: string;
}): JSX.Element {
  const { form, setValue, errorFor, touch, readOnly } = useCms1500Editor();
  const message = errorFor(path);
  return (
    <input
      className={classes(cx.ci, message && cx.ciBad)}
      type={type ?? 'text'}
      value={String(getPath(form, path) ?? '')}
      placeholder={placeholder}
      maxLength={maxLength}
      disabled={readOnly}
      title={message}
      aria-label={label}
      aria-invalid={Boolean(message)}
      autoComplete="off"
      data-claim-field={path}
      onChange={(event) => setValue(path, event.currentTarget.value)}
      onBlur={(event) => {
        if (uppercase && event.currentTarget.value !== event.currentTarget.value.toUpperCase()) {
          setValue(path, event.currentTarget.value.toUpperCase());
        }
        touch(path);
      }}
    />
  );
}

interface ServiceLinesRowProps {
  drawerLine: number | null;
  onDrawerLineChange: (index: number | null) => void;
  issueMessages: Map<string, string>;
}

export function ServiceLinesRow({ drawerLine, onDrawerLineChange, issueMessages }: ServiceLinesRowProps): JSX.Element {
  const { form, update, errorFor, touch, readOnly } = useCms1500Editor();
  const filledLetters = CMS1500_LETTERS.filter((_, index) => form.dx[index].trim());
  const drawer = drawerLine !== null ? form.lines[drawerLine] : undefined;

  const togglePointer = (lineIndex: number, letter: string): void => {
    update((draft) => {
      const ptrs = draft.lines[lineIndex].ptrs;
      const at = ptrs.indexOf(letter);
      if (at >= 0) {
        ptrs.splice(at, 1);
      } else if (ptrs.length < 4) {
        ptrs.push(letter);
      }
    });
    touch(`lines.${lineIndex}.ptrs`);
  };

  return (
    <div className={cx.row} style={{ gridTemplateColumns: 'minmax(0, 1fr)' }}>
      <div className={classes(cx.cell, cx.cellFlush)}>
        <div className={cx.pad}>
          <Head n="24" title="Service lines" />
          {errorFor('lines') && (
            <div className={cx.error} data-claim-field="lines">
              {errorFor('lines')}
            </div>
          )}
        </div>
        <div className={cx.svc}>
          <div className={cx.svcInner}>
            <div className={cx.lineHead}>
              <div />
              <div>
                Dates of service
                <Req show />
              </div>
              <div>
                Place of service
                <Req show />
              </div>
              <div>EMG</div>
              <div>
                Procedures, services, or supplies
                <Req show />
              </div>
              <div>
                Diagnosis codes
                <Req show />
              </div>
            </div>
            {form.lines.map((line, index) => {
              const prefix = `lines.${index}.`;
              const lineErrors = [...issueMessages.entries()]
                .filter(([key]) => key.startsWith(prefix))
                .map(([, message]) => message.replace(/^Line \d+: /, ''));
              const detailError = LINE_DETAIL_FIELDS.some((field) => errorFor(`${prefix}${field}`));
              const stale = line.ptrs.filter((letter) => !filledLetters.includes(letter));
              const summary = lineSummary(line);
              return (
                <div key={`line-${index}`} className={cx.line}>
                  <div className={cx.lineNum}>
                    <span>{index + 1}</span>
                    {!readOnly && (
                      <Menu position="right-start" withinPortal>
                        <Menu.Target>
                          <button type="button" className={cx.dots} aria-label={`Line ${index + 1} actions`}>
                            <IconDots size={16} />
                          </button>
                        </Menu.Target>
                        <Menu.Dropdown>
                          <Menu.Item
                            disabled={form.lines.length >= CMS1500_MAX_LINES}
                            onClick={() =>
                              update((draft) => {
                                draft.lines.splice(
                                  index + 1,
                                  0,
                                  JSON.parse(JSON.stringify(draft.lines[index])) as Cms1500Line
                                );
                              })
                            }
                          >
                            Duplicate line
                          </Menu.Item>
                          <Menu.Item
                            disabled={form.lines.length < 2}
                            onClick={() =>
                              update((draft) => {
                                draft.lines.splice(index, 1);
                              })
                            }
                          >
                            Remove line
                          </Menu.Item>
                        </Menu.Dropdown>
                      </Menu>
                    )}
                  </div>
                  <button
                    type="button"
                    className={classes(cx.lineSub, detailError && cx.lineSubBad)}
                    onClick={() => onDrawerLineChange(index)}
                  >
                    {summary || 'Add charge, units, note, or NDC codes'}
                  </button>
                  <div className={classes(cx.lc, cx.dos)}>
                    <CellInput path={`${prefix}from`} type="date" label={`Line ${index + 1} service date from`} />
                    <span>–</span>
                    <CellInput path={`${prefix}to`} type="date" label={`Line ${index + 1} service date to`} />
                  </div>
                  <div className={cx.lc}>
                    <select
                      className={classes(cx.ci, errorFor(`${prefix}pos`) && cx.ciBad)}
                      value={line.pos}
                      disabled={readOnly}
                      aria-label={`Line ${index + 1} place of service`}
                      data-claim-field={`${prefix}pos`}
                      onChange={(event) => {
                        const value = event.currentTarget.value;
                        update((draft) => {
                          draft.lines[index].pos = value;
                        });
                        touch(`${prefix}pos`);
                      }}
                    >
                      <option value="">Select place of service</option>
                      {CMS1500_PLACES_OF_SERVICE.map(([value, text]) => (
                        <option key={value} value={value}>
                          {text}
                        </option>
                      ))}
                    </select>
                    <IconChevronDown />
                  </div>
                  <div className={classes(cx.lc, cx.emg)}>
                    <input
                      type="checkbox"
                      checked={line.emg}
                      disabled={readOnly}
                      aria-label={`Line ${index + 1} emergency`}
                      onChange={(event) => {
                        const checked = event.currentTarget.checked;
                        update((draft) => {
                          draft.lines[index].emg = checked;
                        });
                      }}
                    />
                  </div>
                  <div className={classes(cx.lc, cx.proc)}>
                    <CellInput
                      path={`${prefix}cpt`}
                      placeholder="Code"
                      maxLength={5}
                      uppercase
                      label={`Line ${index + 1} procedure code`}
                    />
                    {[0, 1, 2, 3].map((mod) => (
                      <CellInput
                        key={mod}
                        path={`${prefix}mods.${mod}`}
                        placeholder="Mod"
                        maxLength={2}
                        uppercase
                        label={`Line ${index + 1} modifier ${mod + 1}`}
                      />
                    ))}
                  </div>
                  <div className={classes(cx.lc, cx.dxc)} data-claim-field={`${prefix}ptrs`}>
                    <span className={cx.ptrs}>
                      {filledLetters.length === 0 && stale.length === 0 ? (
                        <span className={cx.ph}>Add diagnosis codes in box 21</span>
                      ) : (
                        [...filledLetters, ...stale].map((letter) => {
                          const order = line.ptrs.indexOf(letter);
                          const on = order >= 0;
                          return (
                            <button
                              key={letter}
                              type="button"
                              className={classes(cx.ptr, on && cx.ptrOn, stale.includes(letter) && cx.ptrStale)}
                              disabled={readOnly || (!on && line.ptrs.length >= 4)}
                              title={form.dx[CMS1500_LETTERS.indexOf(letter)] || 'No code'}
                              aria-pressed={on}
                              aria-label={`Line ${index + 1} diagnosis pointer ${letter}`}
                              onClick={() => togglePointer(index, letter)}
                            >
                              {letter}
                              {on && line.ptrs.length > 1 ? <sup>{order + 1}</sup> : null}
                            </button>
                          );
                        })
                      )}
                    </span>
                    {!readOnly && (
                      <button type="button" className={cx.more} onClick={() => onDrawerLineChange(index)}>
                        More
                        <IconArrowRight />
                      </button>
                    )}
                  </div>
                  {lineErrors.length > 0 && <div className={cx.lineErr}>{lineErrors.join(' · ')}</div>}
                </div>
              );
            })}
          </div>
        </div>
        <div className={cx.pad}>
          {!readOnly && form.lines.length < CMS1500_MAX_LINES && (
            <button
              type="button"
              className={cx.add}
              onClick={() =>
                update((draft) => {
                  const previous = draft.lines[draft.lines.length - 1];
                  draft.lines.push({
                    ...blankCms1500Line(previous?.pos),
                    from: previous?.from ?? '',
                    to: previous?.to ?? '',
                  });
                })
              }
            >
              <IconPlus />
              Add service line
            </button>
          )}
          {!readOnly && form.lines.length >= CMS1500_MAX_LINES && (
            <div className={cx.hint}>50 service lines is the maximum for one claim.</div>
          )}
        </div>
      </div>

      <Drawer
        opened={drawer !== undefined}
        onClose={() => onDrawerLineChange(null)}
        position="right"
        size={480}
        title={
          drawerLine !== null && drawer ? (
            <div>
              <Text fw={700} size="lg">
                Service line {drawerLine + 1}
                {drawer.cpt ? ` · ${drawer.cpt}` : ''}
              </Text>
              <Text size="sm" c="dimmed">
                Box 24 details sent on the 837P service line
              </Text>
            </div>
          ) : null
        }
      >
        {drawerLine !== null && drawer && (
          <div className={cx.cx} style={{ border: 0, margin: 0 }}>
            <Stack gap="md">
              <div className={cx.grid}>
                <Field code="24F" label="Charges" req>
                  <MoneyIn path={`lines.${drawerLine}.charge`} label="Charges" />
                </Field>
                <Field code="24G" label="Units" req>
                  <TextIn path={`lines.${drawerLine}.units`} label="Units" />
                </Field>
              </div>
              <div className={cx.grid}>
                <Field label="Unit type">
                  <SelectIn path={`lines.${drawerLine}.unitType`} options={CMS1500_UNIT_TYPES} label="Unit type" />
                </Field>
                <Field code="24H" label="EPSDT / Family plan">
                  <SelectIn
                    path={`lines.${drawerLine}.epsdt`}
                    options={[
                      ['Y', 'Y - Yes'],
                      ['N', 'N - No'],
                    ]}
                    label="EPSDT / Family plan"
                  />
                </Field>
              </div>
              <Field code="24J" label="Rendering provider NPI">
                <TextIn
                  path={`lines.${drawerLine}.renderNpi`}
                  maxLength={10}
                  placeholder="9876543210"
                  label="Rendering provider NPI"
                />
                <div className={cx.hint}>
                  Only when it differs from the claim's rendering provider (box 17, tab 82).
                </div>
              </Field>
              <Field code="G1" label="Line prior authorization number">
                <TextIn path={`lines.${drawerLine}.lineAuth`} maxLength={50} label="Line prior authorization number" />
              </Field>
              <Field label="Line note">
                <TextIn path={`lines.${drawerLine}.note`} maxLength={80} label="Line note" />
              </Field>
              <div className={cx.drawerSub}>Drug identification</div>
              <Field code="N4" label="NDC code">
                <TextIn
                  path={`lines.${drawerLine}.ndc`}
                  maxLength={13}
                  placeholder="11 digits, 5-4-2"
                  label="NDC code"
                />
              </Field>
              <div className={cx.grid}>
                <Field label="NDC quantity" req={Boolean(drawer.ndc)}>
                  <TextIn path={`lines.${drawerLine}.ndcQty`} maxLength={10} label="NDC quantity" />
                </Field>
                <Field label="NDC unit" req={Boolean(drawer.ndc)}>
                  <SelectIn path={`lines.${drawerLine}.ndcUnit`} options={CMS1500_NDC_UNITS} label="NDC unit" />
                </Field>
              </div>
              <div className={cx.drawerSub}>Outside lab (box 20)</div>
              <Field label="Purchased service charge">
                <MoneyIn path={`lines.${drawerLine}.labCharge`} label="Purchased service charge" />
              </Field>
              <Field label="Outside lab identifier" req={Boolean(drawer.labCharge)}>
                <TextIn path={`lines.${drawerLine}.labProviderId`} maxLength={50} label="Outside lab identifier" />
              </Field>
              <Group justify="flex-end">
                <Button color="#640040" onClick={() => onDrawerLineChange(null)}>
                  Done
                </Button>
              </Group>
            </Stack>
          </div>
        )}
      </Drawer>
    </div>
  );
}
